import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import { AppError } from "../../../../../../errors/errorProcessors"
import usePublishLCTDeviceAmendment from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDeviceAmendment"
import useSaveLCTDeviceDraft from "../../../../../../services/middleware/hooks/devices/lct/useSaveLCTDeviceDraft"
import { checkContextErrors } from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../../types/providerTypes"
import { EVACDeviceFormData } from "../../../types/providerTypes"

export const AmendEVACDeviceContext = createContext(
    {} as IDeviceFormContextValue<EVACDeviceFormData>
)

type AmendEVACDeviceProviderProps = {
    formData: EVACDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<EVACDeviceFormData>>
    formDataIsLoading: boolean
    systemReference: string
    formDataError?: AppError
    attachmentUUID?: string
    children: ReactNode
}

const AmendEVACDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    systemReference,
    attachmentUUID,
    children,
}: AmendEVACDeviceProviderProps): ReactElement => {
    const [newDeviceId, setNewDeviceId] = useState<string | undefined>()
    const [newDeviceRef, setNewDeviceRef] = useState<string | undefined>()
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishLCTDeviceAmendment,
        isLoading: publishInProgress,
        isLoaded: publishSuccessful,
        error: publishError,
    } = usePublishLCTDeviceAmendment(
        formData,
        LCTOnlyDatasets.EVAC,
        systemReference,
        attachmentUUID
    )

    const {
        saveLCTDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveLCTDeviceDraft<EVACDeviceFormData>(
        true,
        LCTOnlyDatasets.EVAC,
        systemReference,
        attachmentUUID
    )

    const onSubmit = useCallback(async (): Promise<void> => {
        const res = await publishLCTDeviceAmendment()
        setNewDeviceId(res.published_device_id)
        setNewDeviceRef(res.amendment_ref)
    }, [publishLCTDeviceAmendment])

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const value: IDeviceFormContextValue<EVACDeviceFormData> = useMemo(
        () => ({
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            deviceId: newDeviceId,
            deviceRef: newDeviceRef,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer: false,
            formIsLoading: formDataIsLoading,
            saveDraft: saveLCTDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        }),
        [
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            newDeviceId,
            newDeviceRef,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            formDataIsLoading,
            saveLCTDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        ]
    )

    return (
        <AmendEVACDeviceContext.Provider value={value}>
            {children}
        </AmendEVACDeviceContext.Provider>
    )
}

AmendEVACDeviceProvider.defaultProps = {
    formDataError: undefined,
    attachmentUUID: undefined,
}

export default AmendEVACDeviceProvider
